import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AdminOverview } from '../pages/admin/AdminOverview';
import { KycQueue } from '../pages/admin/KycQueue';
import { FleetManagers } from '../pages/admin/FleetManagers';
import { Passengers } from '../pages/admin/Passengers';
import { RoutesPage } from '../pages/admin/RoutesPage';
import { Transactions } from '../pages/admin/Transactions';
import { Assignments } from '../pages/fleet/Assignments';
import { Drivers } from '../pages/fleet/Drivers';
import { FleetDashboard } from '../pages/fleet/FleetDashboard';
import { FleetKycRequired } from '../pages/fleet/FleetKycRequired';
import { FleetMap } from '../pages/fleet/FleetMap';
import { SubFleets } from '../pages/fleet/SubFleets';
import { Vehicles } from '../pages/fleet/Vehicles';
import { IncidentMap } from '../pages/law/IncidentMap';
import { Incidents } from '../pages/law/Incidents';
import { ChangePassword } from '../pages/account/ChangePassword';
import { Profile } from '../pages/account/Profile';
import { AuditLog } from '../pages/system/AuditLog';
import { AdminInvitations } from '../pages/system/AdminInvitations';
import { AdminUsers } from '../pages/system/AdminUsers';
import { SystemHealth } from '../pages/system/SystemHealth';
import { Role, User } from '../types/app';

export const routeTitles: Record<string, string> = {
  '/fleet/dashboard': 'Fleet Dashboard',
  '/fleet/onboarding': 'Fleet Access Pending',
  '/fleet/map': 'Fleet Map',
  '/fleet/assignments': 'Assignments',
  '/fleet/drivers': 'Drivers',
  '/fleet/vehicles': 'Vehicles',
  '/fleet/authority': 'Fleet Organization',
  '/admin/overview': 'Overview',
  '/admin/kyc': 'KYC Queue',
  '/admin/passengers': 'Passengers',
  '/admin/fleet-managers': 'Fleet Managers',
  '/admin/transactions': 'Transactions',
  '/admin/routes': 'Routes',
  '/incidents': 'Incidents',
  '/incidents/all': 'All Incidents',
  '/incidents/map': 'Incident Map',
  '/system/health': 'System Health',
  '/system/audit': 'Audit Log',
  '/system/admin-invitations': 'Admin Invitations',
  '/system/admin-users': 'Admin Users',
  '/account/change-password': 'Change Password',
  '/account/profile': 'My Profile',
};

export function AppRoutes({
  accessToken = '',
  demoMode = false,
  fleetKycApproved = true,
  onFleetKycSubmitted,
  role = '',
  searchQuery = '',
  user = null,
}: {
  accessToken?: string;
  demoMode?: boolean;
  fleetKycApproved?: boolean;
  onFleetKycSubmitted?: (status?: string) => void;
  role?: Role | '';
  searchQuery?: string;
  user?: User | null;
}) {
  const fleetAccess = (element: React.ReactElement) => (
    role === 'fleet_manager' && !demoMode && !fleetKycApproved
      ? <Navigate to="/fleet/onboarding" replace />
      : element
  );

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/fleet/dashboard" replace />} />
      <Route path="/fleet/onboarding" element={<FleetKycRequired onSubmitted={onFleetKycSubmitted} user={user} />} />
      <Route path="/fleet/dashboard" element={fleetAccess(<FleetDashboard demoMode={demoMode} />)} />
      <Route path="/fleet/map" element={fleetAccess(<FleetMap accessToken={accessToken} />)} />
      <Route path="/fleet/assignments" element={fleetAccess(<Assignments />)} />
      <Route path="/fleet/drivers" element={fleetAccess(<Drivers role={role} searchQuery={searchQuery} />)} />
      <Route path="/fleet/vehicles" element={fleetAccess(<Vehicles role={role} searchQuery={searchQuery} />)} />
      <Route path="/fleet/authority" element={fleetAccess(<SubFleets />)} />
      <Route path="/fleet/sub-fleets" element={<Navigate to="/fleet/authority" replace />} />
      <Route path="/admin/overview" element={<AdminOverview />} />
      <Route path="/admin/kyc" element={<KycQueue searchQuery={searchQuery} />} />
      <Route path="/admin/passengers" element={<Passengers searchQuery={searchQuery} />} />
      <Route
        path="/admin/fleet-managers"
        element={role === 'admin' || role === 'super_admin' ? <FleetManagers searchQuery={searchQuery} /> : <Navigate to={getRoleHome(role)} replace />}
      />
      <Route path="/admin/transactions" element={<Transactions />} />
      <Route path="/admin/routes" element={<RoutesPage searchQuery={searchQuery} />} />
      <Route path="/incidents" element={<Incidents activeOnly />} />
      <Route path="/incidents/all" element={<Incidents activeOnly={false} />} />
      <Route path="/incidents/map" element={<IncidentMap />} />
      <Route path="/account/profile" element={user ? <Profile role={role} user={user} /> : <Navigate to={getRoleHome(role)} replace />} />
      <Route path="/account/change-password" element={<ChangePassword />} />
      <Route path="/system/health" element={<SystemHealth />} />
      <Route path="/system/audit" element={<AuditLog searchQuery={searchQuery} />} />
      <Route
        path="/system/admin-invitations"
        element={role === 'super_admin' ? <AdminInvitations searchQuery={searchQuery} /> : <Navigate to={getRoleHome(role)} replace />}
      />
      <Route
        path="/system/admin-users"
        element={role === 'super_admin' ? <AdminUsers searchQuery={searchQuery} /> : <Navigate to={getRoleHome(role)} replace />}
      />
      <Route path="*" element={<Navigate to="/fleet/dashboard" replace />} />
    </Routes>
  );
}

function getRoleHome(role: Role | '') {
  if (role === 'admin') return '/admin/overview';
  if (role === 'law_enforcement') return '/incidents';
  return '/fleet/dashboard';
}
